import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import styles from './PasteMaterial.module.css';

interface IngestResponse {
  id: string;
  status: string;
}

export function PasteMaterial() {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [source, setSource] = useState('manual');
  const [url, setUrl] = useState('');
  const [success, setSuccess] = useState(false);

  const queryClient = useQueryClient();

  const ingestMutation = useMutation({
    mutationFn: async () => {
      const materialUrl = url || `manual://${Date.now()}`;

      return apiClient.post<IngestResponse>('/ingest/material', {
        source,
        url: materialUrl,
        title: title || 'Breaking News',
        content,
      });
    },
    onSuccess: () => {
      setSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['feed'] });

      setTimeout(() => {
        resetForm();
      }, 2000);
    },
  });

  const resetForm = () => {
    setTitle('');
    setContent('');
    setUrl('');
    setSource('manual');
    setSuccess(false);
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button className={styles.openButton} onClick={() => setIsOpen(true)}>
        + Clip Material
      </button>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{success ? 'Clipped!' : 'Clip Material'}</h3>
        <button className={styles.closeButton} onClick={resetForm}>
          ×
        </button>
      </div>

      {success ? (
        <div className={styles.success}>
          Material saved. Run /ingest to extract claims.
        </div>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            ingestMutation.mutate();
          }}
        >
          <div className={styles.field}>
            <label htmlFor="source">Source</label>
            <select
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="manual">Manual Entry</option>
              <option value="nytimes">New York Times</option>
              <option value="nbc-news">NBC News</option>
              <option value="abc-news">ABC News</option>
              <option value="ap-news">AP News</option>
              <option value="reuters">Reuters</option>
              <option value="cbs-news">CBS News</option>
              <option value="cnn">CNN</option>
              <option value="fox-news">Fox News</option>
              <option value="bloomberg">Bloomberg</option>
              <option value="msnbc">MSNBC</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="url">URL (optional)</label>
            <input
              id="url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Material headline"
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="content">Content</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste material content here..."
              rows={10}
              required
            />
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={resetForm}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={ingestMutation.isPending || !content.trim() || !title.trim()}
            >
              {ingestMutation.isPending ? 'Saving...' : 'Clip'}
            </button>
          </div>

          {ingestMutation.isError && (
            <div className={styles.error}>
              Error: {ingestMutation.error instanceof Error ? ingestMutation.error.message : 'Unknown error'}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
